import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Capacitor } from '@capacitor/core';
import { NotificationProvider } from './contexts/NotificationContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { PromptProvider } from './contexts/PromptContext';
import ProtectedRoute from './components/ProtectedRoute';
import useAuthStore from './store/authStore';
import useTenantStore from './store/tenantStore';
import { tenancyService, userService } from './services/api';
import i18n from './i18n';
import { initLocalNotifications, onLocalNotificationTap, showLocalNotification } from './utils/localNotifications';
import { initPushNotifications, unregisterPushNotifications, onPushNotificationTap, onPushNotificationReceived } from './utils/pushNotifications';
import './App.css';
import PublicBlog from './pages/PublicBlog';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import RoleDashboard from './pages/RoleDashboard';
import Courses from './pages/Courses';
import ModuleDetail from './pages/ModuleDetail';
import Store from './pages/Store';
import CreateModule from './pages/CreateModule';
import EditModule from './pages/EditModule';
import CreateCategory from './pages/CreateCategory';
import EditCategory from './pages/EditCategory';
import CreateLesson from './pages/CreateLesson';
import EditLesson from './pages/EditLesson';
import LessonDetail from './pages/LessonDetail';
import TakeQuiz from './pages/TakeQuiz';
import EditQuiz from './pages/EditQuiz';
import Calendar from './pages/Calendar';
import Messages from './pages/Messages';
import Announcements from './pages/Announcements';
import Profile from './pages/Profile';
import MyEnrollments from './pages/MyEnrollments';
import TeacherStudents from './pages/TeacherStudents';
import TeacherEnrollments from './pages/TeacherEnrollments';
import TeacherBlog from './pages/TeacherBlog';
import TeacherShare from './pages/TeacherShare';
import TeacherDocuments from './pages/TeacherDocuments';
import TeacherComments from './pages/TeacherComments';
import NotFound from './pages/NotFound';
import Resources from './pages/Resources';
import TeacherProfile from './pages/TeacherProfile';
import TeachersDirectory from './pages/TeachersDirectory';
import PaymentAccounts from './pages/PaymentAccounts';
import PaymentPlans from './pages/PaymentPlans';
import ContactUs from './pages/ContactUs';

// Admin portal
import AdminUsers from './pages/admin/AdminUsers';
import AdminTeacherApprovals from './pages/admin/AdminTeacherApprovals';
import AdminModeration from './pages/admin/AdminModeration';
import AdminSubscriptions from './pages/admin/AdminSubscriptions';
import AdminGrades from './pages/admin/AdminGrades';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminConnections from './pages/admin/AdminConnections';
import AdminParentLinks from './pages/admin/AdminParentLinks';
import AdminClasses from './pages/admin/AdminClasses';
import AdminTeachers from './pages/admin/AdminTeachers';
import AdminAnnouncements from './pages/admin/AdminAnnouncements';
import AdminSchoolSettings from './pages/admin/AdminSchoolSettings';
import AdminAttendance from './pages/admin/AdminAttendance';
import AdminPayments from './pages/admin/AdminPayments';
import AdminPaymentAccounts from './pages/admin/AdminPaymentAccounts';
import AdminSponsorships from './pages/admin/AdminSponsorships';
import AdminMonitoring from './pages/admin/AdminMonitoring';
import AdminContactMessages from './pages/admin/AdminContactMessages';
import AdminInstagramCampaigns from './pages/admin/AdminInstagramCampaigns';
import AdminTeacherBlogs from './pages/admin/AdminTeacherBlogs';
import AdminEnrollments from './pages/admin/AdminEnrollments';
import PlatformSchools from './pages/platform/PlatformSchools';

// Parent portal
import ParentLinkChild from './pages/parent/ParentLinkChild';
import ParentChildProgress from './pages/parent/ParentChildProgress';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// The marketing landing page only makes sense on the web: a native app user
// already installed the app, so the root route should drop them straight
// into the login/dashboard flow instead of a "why you should use this" page.
// A logged-in web user can still browse it though — it's their own site's
// public page, not a dead end — Landing shows a "Dashboard" link instead of
// Login/Register when authenticated.
function RootRoute() {
  if (Capacitor.isNativePlatform()) return <Navigate to="/login" replace />;
  return <Landing />;
}

function TenantBlogRoute() {
  const { subdomain } = useParams();
  return <PublicBlog subdomain={subdomain} />;
}

// Local notifications stand in for real push here (see utils/localNotifications.js
// for why): request permission once logged in, and route a tap to the
// relevant screen. Native-only — no-ops on web.
function LocalNotificationsManager() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;
    initLocalNotifications();
    let unsubscribe;
    onLocalNotificationTap((data) => {
      if (data?.target_url) navigate(data.target_url);
    }).then((fn) => { unsubscribe = fn; });
    return () => unsubscribe?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return null;
}

// Real push (FCM) - reaches the device even if the app process isn't
// running, which LocalNotificationsManager above can't do (it only reacts
// to events the app witnesses live). Requires FIREBASE_CREDENTIALS_JSON on
// the backend and the native Firebase config files; harmlessly does
// nothing without them. Foreground pushes don't show a system banner on
// their own, so one is rendered locally when it arrives while the app is
// open.
function PushNotificationsManager() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;
    initPushNotifications();
    let unsubTap;
    let unsubReceived;
    onPushNotificationTap((data) => {
      if (data?.target_url) navigate(data.target_url);
    }).then((fn) => { unsubTap = fn; });
    onPushNotificationReceived((notification) => {
      const title = notification.title || notification.data?.title;
      const body = notification.body || notification.data?.body;
      if (title || body) showLocalNotification(title || '', body || '', notification.data || {});
    }).then((fn) => { unsubReceived = fn; });
    return () => {
      unsubTap?.();
      unsubReceived?.();
      unregisterPushNotifications();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return null;
}

// The persisted user object (localStorage, via zustand's persist middleware)
// only ever gets written at login — anything that changes it server-side
// afterwards (email verification, teacher approval, role changes...) would
// otherwise stay invisible in the UI until the user logs out and back in.
function UserRefresher() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const updateUser = useAuthStore((state) => state.updateUser);

  useEffect(() => {
    if (!isAuthenticated) return;
    userService.getMe().then((res) => updateUser(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return null;
}

// A teacher blog subdomain (e.g. johndoe.lmcid.com) renders a completely
// separate standalone page instead of the LMS app - no sidebar/dashboard/
// auth-gated navigation, just the public blog. `www.lmcid.com`, the bare
// apex, and local dev hosts (localhost/LAN IP) all fall through to the
// normal app below.
function getHostedLabel() {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  const match = host.match(/^([a-z0-9-]+)\.lmcid\.com$/i);
  if (!match) return null;
  const subdomain = match[1].toLowerCase();
  return ['www', 'panel', 'platform'].includes(subdomain) ? null : subdomain;
}

function HostedApplication({ children }) {
  const hostedLabel = getHostedLabel();
  const isNative = Capacitor.isNativePlatform();
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isIpAddress = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host) || host.includes(':');
  const isCoreHost = !host || host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local') || isIpAddress ||
    ['lmcid.com', 'www.lmcid.com', 'panel.lmcid.com', 'platform.lmcid.com'].includes(host);
  const [resolution, setResolution] = useState(isNative || isCoreHost ? { kind: 'application' } : null);
  const setTenantResolution = useTenantStore((state) => state.setResolution);

  useEffect(() => {
    if (isNative || isCoreHost) return undefined;
    let mounted = true;
    tenancyService.resolveHost()
      .then((response) => {
        if (mounted) {
          setResolution(response.data);
          setTenantResolution(response.data);
          const schoolLocale = response.data?.school?.locale;
          if (schoolLocale && !localStorage.getItem('i18nextLng')) i18n.changeLanguage(schoolLocale);
        }
      })
      .catch(() => { if (mounted) setResolution({ kind: 'unknown' }); });
    return () => { mounted = false; };
  }, [host, isCoreHost, isNative, setTenantResolution]);

  if (!resolution) {
    return <div className="min-h-screen grid place-items-center bg-gray-50"><div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-600" /></div>;
  }
  if (resolution.kind === 'blog') {
    return (
      <NotificationProvider>
        <PublicBlog subdomain={resolution.subdomain || hostedLabel} />
      </NotificationProvider>
    );
  }
  if (resolution.kind === 'unknown') {
    return <div className="min-h-screen grid place-items-center bg-gray-50 p-6 text-center"><div><h1 className="text-3xl font-bold text-gray-900">School not found</h1><p className="mt-3 text-gray-500">This domain is not connected to an active L’mcid school.</p></div></div>;
  }
  return children;
}

function EntitlementRoute({ feature, children }) {
  const enabled = useTenantStore((state) => state.isFeatureEnabled(feature));
  return enabled ? children : <Navigate to="/" replace />;
}

function App() {

  return (
    <HostedApplication><QueryClientProvider client={queryClient}>
      <NotificationProvider>
        <ConfirmProvider>
        <PromptProvider>
        <Router>
          <div className="App">
            <LocalNotificationsManager />
            <PushNotificationsManager />
            <UserRefresher />
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/contact" element={<EntitlementRoute feature="contact_messages"><ContactUs /></EntitlementRoute>} />
              <Route path="/blog/:subdomain" element={<TenantBlogRoute />} />

              <Route path="/dashboard" element={<ProtectedRoute><RoleDashboard /></ProtectedRoute>} />
              <Route path="/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
              <Route path="/store" element={<Store />} />
              <Route path="/modules/:id" element={<ProtectedRoute><ModuleDetail /></ProtectedRoute>} />
              <Route path="/teachers" element={<EntitlementRoute feature="teacher_profiles"><TeachersDirectory /></EntitlementRoute>} />
              <Route path="/teachers/:id" element={<EntitlementRoute feature="teacher_profiles"><TeacherProfile /></EntitlementRoute>} />
              <Route path="/modules/create" element={<ProtectedRoute roles={['teacher', 'administrator']}><CreateModule /></ProtectedRoute>} />
              <Route path="/modules/:id/edit" element={<ProtectedRoute roles={['teacher', 'administrator']}><EditModule /></ProtectedRoute>} />
              <Route path="/modules/:moduleId/categories/create" element={<ProtectedRoute roles={['teacher']}><CreateCategory /></ProtectedRoute>} />
              <Route path="/modules/:moduleId/categories/:categoryId/edit" element={<ProtectedRoute roles={['teacher']}><EditCategory /></ProtectedRoute>} />
              <Route path="/modules/:moduleId/lessons/create" element={<ProtectedRoute roles={['teacher']}><CreateLesson /></ProtectedRoute>} />
              <Route path="/modules/:moduleId/lessons/:lessonId/edit" element={<ProtectedRoute roles={['teacher']}><EditLesson /></ProtectedRoute>} />
              <Route path="/modules/:moduleId/lessons/:lessonId" element={<ProtectedRoute><LessonDetail /></ProtectedRoute>} />
              <Route path="/quizzes/:quizId" element={<ProtectedRoute feature="quizzes"><TakeQuiz /></ProtectedRoute>} />
              <Route path="/quizzes/:quizId/edit" element={<ProtectedRoute roles={['teacher']} feature="quizzes"><EditQuiz /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute feature="calendar"><Calendar /></ProtectedRoute>} />
              <Route path="/resources" element={<ProtectedRoute feature="resources"><Resources /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute feature="messaging"><Messages /></ProtectedRoute>} />
              <Route path="/announcements" element={<ProtectedRoute feature="announcements"><Announcements /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/enrollments" element={<ProtectedRoute roles={['student']}><MyEnrollments /></ProtectedRoute>} />
              <Route path="/students" element={<ProtectedRoute roles={['teacher']}><TeacherStudents /></ProtectedRoute>} />
              <Route path="/teacher/enrollments" element={<ProtectedRoute roles={['teacher']}><TeacherEnrollments /></ProtectedRoute>} />
              <Route path="/teacher/blog" element={<ProtectedRoute roles={['teacher']} feature="teacher_blogs"><TeacherBlog /></ProtectedRoute>} />
              <Route path="/teacher/share" element={<ProtectedRoute roles={['teacher']} feature="qr_sharing"><TeacherShare /></ProtectedRoute>} />
              <Route path="/teacher/documents" element={<ProtectedRoute roles={['teacher']} feature="resources"><TeacherDocuments /></ProtectedRoute>} />
              <Route path="/teacher/comments" element={<ProtectedRoute roles={['teacher']} feature="community_feed"><TeacherComments /></ProtectedRoute>} />
              <Route path="/payment-accounts" element={<ProtectedRoute roles={['teacher']} feature="payments"><PaymentAccounts /></ProtectedRoute>} />
              <Route path="/payment-plans" element={<ProtectedRoute roles={['teacher']} feature="payments"><PaymentPlans /></ProtectedRoute>} />

              {/* Administrator portal */}
              <Route path="/admin/users" element={<ProtectedRoute roles={['administrator']}><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/teacher-approvals" element={<ProtectedRoute roles={['administrator']}><AdminTeacherApprovals /></ProtectedRoute>} />
              <Route path="/admin/moderation" element={<ProtectedRoute roles={['administrator']} feature="ratings"><AdminModeration /></ProtectedRoute>} />
              <Route path="/admin/subscriptions" element={<ProtectedRoute roles={['administrator']} feature="payments"><AdminSubscriptions /></ProtectedRoute>} />
              <Route path="/admin/grades" element={<ProtectedRoute roles={['administrator']}><AdminGrades /></ProtectedRoute>} />
              <Route path="/admin/classes" element={<ProtectedRoute roles={['administrator']}><AdminClasses /></ProtectedRoute>} />
              <Route path="/admin/teachers" element={<ProtectedRoute roles={['administrator']}><AdminTeachers /></ProtectedRoute>} />
              <Route path="/admin/announcements" element={<ProtectedRoute roles={['administrator']} feature="announcements"><AdminAnnouncements /></ProtectedRoute>} />
              <Route path="/admin/school-settings" element={<ProtectedRoute roles={['administrator']}><AdminSchoolSettings /></ProtectedRoute>} />
              <Route path="/admin/attendance" element={<ProtectedRoute roles={['administrator']} feature="attendance"><AdminAttendance /></ProtectedRoute>} />
              <Route path="/admin/payments" element={<ProtectedRoute roles={['administrator']} feature="payments"><AdminPayments /></ProtectedRoute>} />
              <Route path="/admin/payment-accounts" element={<ProtectedRoute roles={['administrator']} feature="payments"><AdminPaymentAccounts /></ProtectedRoute>} />
              <Route path="/admin/sponsorships" element={<ProtectedRoute roles={['administrator']} feature="featured_content"><AdminSponsorships /></ProtectedRoute>} />
              <Route path="/admin/audit-log" element={<ProtectedRoute roles={['administrator']} feature="audit_logs"><AdminAuditLog /></ProtectedRoute>} />
              <Route path="/admin/connections" element={<ProtectedRoute roles={['administrator']} feature="activity_logs"><AdminConnections /></ProtectedRoute>} />
              <Route path="/admin/parent-links" element={<ProtectedRoute roles={['administrator']} feature="parent_portal"><AdminParentLinks /></ProtectedRoute>} />
              <Route path="/admin/monitoring" element={<ProtectedRoute roles={['administrator']} feature="monitoring"><AdminMonitoring /></ProtectedRoute>} />
              <Route path="/admin/contact-messages" element={<ProtectedRoute roles={['administrator']} feature="contact_messages"><AdminContactMessages /></ProtectedRoute>} />
              <Route path="/admin/instagram-campaigns" element={<ProtectedRoute roles={['administrator']} feature="instagram"><AdminInstagramCampaigns /></ProtectedRoute>} />
              <Route path="/admin/teacher-blogs" element={<ProtectedRoute roles={['administrator']} feature="teacher_blogs"><AdminTeacherBlogs /></ProtectedRoute>} />
              <Route path="/admin/enrollments" element={<ProtectedRoute roles={['administrator']}><AdminEnrollments /></ProtectedRoute>} />
              <Route path="/platform/schools" element={<ProtectedRoute platformOwner><PlatformSchools /></ProtectedRoute>} />

              {/* Parent portal */}
              <Route path="/parent/link-child" element={<ProtectedRoute roles={['parent', 'student']} feature="parent_portal"><ParentLinkChild /></ProtectedRoute>} />
              <Route path="/family-links" element={<ProtectedRoute roles={['parent', 'student']} feature="parent_portal"><ParentLinkChild /></ProtectedRoute>} />
              <Route path="/family-link" element={<ProtectedRoute roles={['parent', 'student']} feature="parent_portal"><ParentLinkChild /></ProtectedRoute>} />
              <Route path="/parent/children/:childId" element={<ProtectedRoute roles={['parent']} feature="parent_portal"><ParentChildProgress /></ProtectedRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </Router>
        </PromptProvider>
        </ConfirmProvider>
      </NotificationProvider>
    </QueryClientProvider></HostedApplication>
  );
}

export default App;
